import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, TrendingUp, Clock, BarChart3, Target, Zap, Users, ArrowRight, Play } from 'lucide-react';

const SaaSPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-b from-background to-secondary/10">
        <div className="max-w-6xl mx-auto text-center">
          <Badge variant="secondary" className="mb-6">
            For SaaS Marketing Teams
          </Badge>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            Stop Burning Runway on $80 B2B Clicks.
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Enterprise keywords are expensive. Upfixe strictly enforces your Target CPA, automatically filtering out keywords that would drain your budget with zero sign-ups.
          </p>
          <div className="flex justify-center mb-12">
            <Button size="lg" className="text-lg px-8 py-6">
              Get a Free Account
              <ArrowRight className="ml-2" />
            </Button>
          </div>
          <div className="flex items-center justify-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              No credit card required
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              Setup in under 5 minutes
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              Cancel anytime
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              The SaaS Marketing Challenge
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Every SaaS marketer faces the same impossible equation: generate more qualified leads, 
              prove clear ROI, and do it all with limited time and budget.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 border-2 border-destructive/20">
              <CardContent className="p-0">
                <Target className="w-8 h-8 text-destructive mb-4" />
                <h3 className="text-xl font-semibold mb-3">The High CPC Trap</h3>
                <p className="text-muted-foreground">
                  B2B software terms often cost $50 to $100 per click. Spending $5,000 to get a handful of clicks and zero demos burns runway fast.
                </p>
              </CardContent>
            </Card>
            
            <Card className="p-6 border-2 border-destructive/20">
              <CardContent className="p-0">
                <BarChart3 className="w-8 h-8 text-destructive mb-4" />
                <h3 className="text-xl font-semibold mb-3">ROI Pressure</h3>
                <p className="text-muted-foreground">
                  Leadership demands clear metrics on CAC (Customer Acquisition Cost), but broad match keywords ruin your averages.
                </p>
              </CardContent>
            </Card>
            
            <Card className="p-6 border-2 border-destructive/20">
              <CardContent className="p-0">
                <Clock className="w-8 h-8 text-destructive mb-4" />
                <h3 className="text-xl font-semibold mb-3">Rapid Deployment Needs</h3>
                <p className="text-muted-foreground">
                  New features launch constantly, but creating campaigns for each one 
                  takes too long and requires expensive agency help.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 px-4 bg-secondary/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              AI-Powered Campaigns That Actually Work
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our AI understands SaaS businesses. Create campaigns for every feature launch, 
              get qualified leads, and prove ROI with actionable reports—all in minutes, not weeks.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Zap className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Find Affordable Long-Tail Terms</h3>
                    <p className="text-muted-foreground">
                      Our AI extracts high-intent, long-tail keywords directly from your feature landing pages that competitors aren't overbidding on.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <TrendingUp className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Enforce Target CPA</h3>
                    <p className="text-muted-foreground">
                      Mechanically reject any keyword where the average CPC would make it impossible to hit your target CAC.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Users className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Quality Over Quantity</h3>
                    <p className="text-muted-foreground">
                      AI optimizes for qualified leads that match your ICP, 
                      reducing CAC and improving conversion rates.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <Card className="p-8">
              <CardContent className="p-0 space-y-4">
                <div className="bg-primary/10 p-4 rounded-lg">
                  <h4 className="font-semibold text-primary mb-2">Before: 2-3 weeks</h4>
                  <p className="text-sm text-muted-foreground">
                    Brainstorm copy → Create campaigns → A/B test → Optimize → Report
                  </p>
                </div>
                <div className="bg-green-100 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">After: 5 minutes</h4>
                  <p className="text-sm text-green-700">
                    Paste URL → AI creates everything → Launch → Get reports
                  </p>
                </div>
                <div className="text-center pt-4">
                  <Badge variant="secondary">94% time savings</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-background to-secondary/20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              See How SaaS Teams Scale Their Marketing
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Watch how SaaS marketing teams create high-converting campaigns for every feature launch. 
              From idea to qualified leads in minutes, not weeks.
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto mb-12">
            <div className="aspect-video rounded-2xl overflow-hidden border-2 border-primary/20">
              <iframe
                src="https://www.youtube.com/embed/G9TxeAKXtbk"
                title="Upfixe SaaS Demo Video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="text-lg px-8"
            >
              Get a Free Account <ArrowRight className="ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="text-lg px-8"
              onClick={() => window.open('https://youtu.be/G9TxeAKXtbk', '_blank')}
            >
              <Play className="mr-2 h-5 w-5" />
              Watch Full Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Built for SaaS Marketing Teams
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <CardContent className="p-0 text-center">
                <Zap className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">5-Minute Feature Campaigns</h3>
                <p className="text-sm text-muted-foreground">
                  Launch campaigns for new features instantly with AI-generated copy
                </p>
              </CardContent>
            </Card>
            
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <CardContent className="p-0 text-center">
                <Target className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Perfect Landing Page Copy</h3>
                <p className="text-sm text-muted-foreground">
                  Get campaigns for all your landing pages with copy that converts
                </p>
              </CardContent>
            </Card>
            
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <CardContent className="p-0 text-center">
                <BarChart3 className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Actionable Reports</h3>
                <p className="text-sm text-muted-foreground">
                  ROI-focused reporting that leadership actually wants to see
                </p>
              </CardContent>
            </Card>
            
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <CardContent className="p-0 text-center">
                <Clock className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Rapid Deployment</h3>
                <p className="text-sm text-muted-foreground">
                  No dev resources needed. Launch campaigns as fast as you ship features
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 px-4 bg-secondary/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12">
            Trusted by SaaS Marketing Teams
          </h2>
          
          <Card className="p-8 border-2">
            <CardContent className="p-0">
              <div className="text-lg italic mb-6">
                "We launched campaigns for 12 new features in our first month. Our CAC dropped 
                by 40% and lead quality improved dramatically. The ROI reports made our CFO happy for once."
              </div>
              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-semibold">Sarah Chen</div>
                  <div className="text-sm text-muted-foreground">Marketing Manager, TechFlow SaaS</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">68%</div>
              <div className="text-sm text-muted-foreground">Higher Lead Quality</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">5min</div>
              <div className="text-sm text-muted-foreground">Average Campaign Setup</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">3.2x</div>
              <div className="text-sm text-muted-foreground">ROI Improvement</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              From Feature Launch to Leads in 3 Steps
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-semibold mb-3">Connect Your Landing Pages</h3>
              <p className="text-muted-foreground">
                Paste URLs for your feature pages, product tours, or any landing page you want to promote.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-semibold mb-3">AI Creates Perfect Campaigns</h3>
              <p className="text-muted-foreground">
                Our AI analyzes your pages and generates optimized ad copy, targeting, and campaign structure.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-semibold mb-3">Launch & Get Actionable Reports</h3>
              <p className="text-muted-foreground">
                Launch immediately and receive reports that show clear ROI, lead quality, and optimization suggestions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-primary/10 to-secondary/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your SaaS Marketing?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Join hundreds of SaaS teams who've already improved their lead quality and proven ROI.
          </p>
          
          <div className="flex justify-center mb-8">
            <Button size="lg" className="text-lg px-8 py-6">
              Get a Free Account
              <ArrowRight className="ml-2" />
            </Button>
          </div>
          
          <div className="flex items-center justify-center gap-8 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              Free plan available
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              No setup fees
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              Results in just 5 mins
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default SaaSPage;